import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { selectAllFareFamilyFlights, selectSelectedFareFamilyFlightId, setSelectedFareFamilyFlightId } from "../../model"
import { IFareFamilyParameter, IFlight } from "@/shared/api/aviabooking"
import CheckSvg from '@/shared/assets/images/Check.svg'
import XSvg from '@/shared/assets/images/X.svg'
import WarningSvg from '@/shared/assets/images/Warning.svg'

const PARAM_LABELS: Record<string, string> = {
    carry_on: 'Ручная кладь',
    baggage: 'Багаж',
    exchangeable: 'Обмен',
    refundable: 'Возврат',
    seats_registration: 'Выбор места',
}

const DISPLAYED_PARAMS = ['carry_on', 'baggage', 'exchangeable', 'refundable', 'seats_registration']

const getRuText = (langs: { code: string; value: string }[]): string =>
    langs.find((l) => l.code === 'RU')?.value ?? ''

const getFlightTotalPrice = (flight: IFlight): { total: number; currency: string } => {
    const fares = flight.prices?.[0]?.fares ?? []
    const total = fares.reduce((sum, f) => sum + f.total_fare * f.quantity, 0)
    const currency = fares[0]?.currency ?? 'RUB'
    return { total, currency }
}

const NeedToPayIcon = ({ needToPay }: { needToPay: string }) => {
    if (needToPay === 'Free') {
        return (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white shrink-0">
                <CheckSvg width={12} height={12} />
            </span>
        )
    }
    if (needToPay === 'Charge') {
        return (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-white shrink-0">
                <WarningSvg width={12} height={12} />
            </span>
        )
    }
    return (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-300 text-white shrink-0">
            <XSvg width={12} height={12} />
        </span>
    )
}

const ParamRow = ({ param }: { param: IFareFamilyParameter }) => {
    const shortText = getRuText(param.short_description)
    const fullText = getRuText(param.full_description)
    const label = PARAM_LABELS[param.code] ?? param.code

    return (
        <div className="flex items-start gap-3 py-2">
            <NeedToPayIcon needToPay={param.need_to_pay} />
            <div className="min-w-0">
                <div className="text-sm font-medium text-gray-700">{label}</div>
                {shortText && (
                    <div className="text-sm text-gray-500 mt-0.5">{shortText}</div>
                )}
                {fullText && fullText !== shortText && (
                    <div className="text-xs text-gray-400 mt-0.5">{fullText}</div>
                )}
            </div>
        </div>
    )
}

export const FareFamilies = () => {
    const dispatch = useAppDispatch()
    const allFlights = useAppSelector(selectAllFareFamilyFlights)
    const selectedFlightId = useAppSelector(selectSelectedFareFamilyFlightId)

    const selectedFlight = allFlights.find((f) => f.id === selectedFlightId) ?? allFlights[0]
    const fareDesc = selectedFlight?.fare_families_descs?.[0]

    const displayedParams = fareDesc?.params
        .filter((p) => DISPLAYED_PARAMS.includes(p.code))
        .sort((a, b) => a.priority - b.priority) ?? []

    if (!allFlights.length) return null

    return (
        <div className="bg-white rounded-3xl p-4 flex flex-col gap-4">
            <div>
                <div className="text-lg font-bold mb-3">Тарифы</div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                    {allFlights.map((flight) => {
                        const desc = flight.fare_families_descs?.[0]
                        const { total, currency } = getFlightTotalPrice(flight)
                        const isSelected = flight.id === selectedFlightId

                        return (
                            <button
                                key={flight.id}
                                type="button"
                                onClick={() => dispatch(setSelectedFareFamilyFlightId(flight.id))}
                                className={`shrink-0 rounded-2xl p-4 min-w-[140px] text-left transition border ${
                                    isSelected
                                        ? 'bg-color-primary border-color-primary text-white'
                                        : 'bg-white border-gray-200 hover:border-color-primary'
                                }`}
                            >
                                <div className={`font-bold text-base mb-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                    {desc?.name ?? '—'}
                                </div>
                                <div className={`text-sm font-semibold ${isSelected ? 'text-white/90' : 'text-color-primary'}`}>
                                    {total.toLocaleString('ru-RU')} {currency === 'RUB' ? '₽' : currency}
                                </div>
                            </button>
                        )
                    })}
                </div>
            </div>

            {fareDesc && displayedParams.length > 0 && (
                <div>
                    <div className="text-base font-bold mb-1">Условия тарифа</div>
                    <div className="divide-y divide-gray-100">
                        {displayedParams.map((param) => (
                            <ParamRow key={param.code} param={param} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
